import {withWriteIntents, decisionValue} from './write-intents';
import { empty, equal, intval, strval, strcasecmp, phpCopy, floatval, array_rand, PhpArray } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { isArray, slot, values, array, unsetPath, type Arr } from './access';
import constants from './digsite-source-constants.json';
export interface DigsiteLootDeps {
    build_full_item_type_arr(type: string): unknown;
    layer_value(row: Arr, key: string): unknown;
    layer_has(row: Arr, key: string): boolean;
    item_field(row: unknown, key: string): unknown;
    ft_digsite_item_arr_is_ungated(row: Arr): boolean;
    get_enchantment_template_id(base: string, innate: Arr, atWill: Arr, charges: number,
        spells: Arr, abilities: Arr, rechargeable: boolean, passives: Arr): string;
    inventory_set(row: PhpRef<Arr>, key: string, value: unknown): void;
    ft_digsite_tier_for_dc(dc: number): number;
    ft_digsite_value_threshold_gp(dc: number): number;
    ft_digsite_roll_table_attachment(id: string, miss?: PhpRef<string | null>): Arr | null;
    ft_digsite_random_enchanted_gear_spec(tier: number): Arr | null;
    ft_digsite_random_gemstone_spec(tier: number, jackpot?: boolean): Arr | null;
    ft_digsite_row_value_gp(row: Arr, qty: number): unknown;
}
/** PHP: ft_digsite_eligible_spells_at_level (includes/functions/inventory/digsite_loot_inc.php:263) */
export function ftDigsiteEligibleSpellsAtLevel(level: number, env: PortEnv<DigsiteLootDeps>): Arr {
    const pool = env.deps.build_full_item_type_arr('spell');
    if (!isArray(pool)) return [];
    const out: Arr[] = [];
    for (const spell of values(pool)) {
        const row = spell as Arr;
        if (env.deps.layer_value(row, 'spell_level') !== null && equal(env.deps.layer_value(row, 'spell_level'), level)
            && !env.deps.layer_has(row, 'consumes_ability_charges') && !env.deps.layer_has(row, 'add_charges_to_ability')
            && !env.deps.layer_has(row, 'requires_class') && !env.deps.layer_has(row, 'requires_subclass')
            && env.deps.ft_digsite_item_arr_is_ungated(row)) out.push(phpCopy(row));
    }
    return out;
}
/** PHP: ft_random_scroll_inventory_spec (includes/functions/inventory/digsite_loot_inc.php:293) */
export function ftRandomScrollInventorySpec(level: number | null = null, env: PortEnv<DigsiteLootDeps>) {
    return withWriteIntents(env, env => {
        level = level !== null && level >= 0 ? level : env.rand(1, 3);
        const spells = array(ftDigsiteEligibleSpellsAtLevel(level, env));
        if (empty(spells)) return null;
        const spell = spells.get(array_rand(spells, 1, env) as string | number) as Arr;
        const id = env.deps.item_field(spell, 'item_id');
        const charges = Math.max(1, Number(intval(env.deps.layer_value(spell, 'spell_level') ?? 1)));
        let template: string;
        try { template = env.deps.get_enchantment_template_id(constants.SPELL_SCROLL_BASE, [], [], charges, [id], [], false, []); }
        catch (error) {
            // PHP catches Exception, not Error/TypeError.
            if (error && typeof error === 'object' && !['Error', 'TypeError', 'ValueError'].includes(strval(slot(error, 'name')))) return null;
            throw error;
        }
        const inventory = { value: phpCopy(spell) };
        env.deps.inventory_set(inventory, 'inventory_item_id', constants.SPELL_SCROLL_BASE);
        unsetPath(inventory.value, ['item_picture']);
        const name = 'Scroll of ' + strval(env.deps.item_field(inventory.value, 'item_name'));
        env.deps.inventory_set(inventory, 'inventory_name', name);
        return { inventory_item_arr: inventory.value, enchantment_template_id: template, qty: 1,
            value_gp: floatval(Math.max(25, level * level * 25)), item_name: name };
    });
}
export function lootSpecForHit(hit: Arr, tier: number, env: PortEnv<DigsiteLootDeps>, attached?: string): Arr | null {
    const row = slot(hit, 'item_arr') as Arr;
    const id = attached ?? strval(env.deps.item_field(row, 'item_id') ?? slot(row, 'item_id') ?? '');
    if (strcasecmp(id, constants.ENCHANTED_GEAR_MARKER) === 0) return env.deps.ft_digsite_random_enchanted_gear_spec(tier);
    if (strcasecmp(id, constants.GEMSTONE_MARKER) === 0) return env.deps.ft_digsite_random_gemstone_spec(tier);
    if (strcasecmp(id, constants.GEMSTONE_JACKPOT_MARKER) === 0) return env.deps.ft_digsite_random_gemstone_spec(tier, true);
    if (strcasecmp(id, constants.SPELL_SCROLL_BASE) === 0) return decisionValue(env, ftRandomScrollInventorySpec(Math.min(3, Math.max(1, tier)), env));
    const qty = Number(intval(slot(hit, 'qty')));
    return { inventory_item_arr: phpCopy(row), enchantment_template_id: '', qty,
        value_gp: env.deps.ft_digsite_row_value_gp(row, qty), item_name: strval(env.deps.item_field(row, 'item_name') ?? '') };
}
/** PHP: ft_digsite_roll_loot (includes/functions/inventory/digsite_loot_inc.php:614) */
export function ftDigsiteRollLoot(dc: number, env: PortEnv<DigsiteLootDeps>) {
    return withWriteIntents(env, env => {
        const tier = env.deps.ft_digsite_tier_for_dc(dc), threshold = env.deps.ft_digsite_value_threshold_gp(dc);
        const master = (constants.MASTER_TABLES as Record<string, string>)[tier];
        if (master === undefined) return [];
        const specs: Arr[] = []; let value = 0, rolls = 0, misses = 0;
        while (value < threshold && rolls < 12 && misses < 12) {
            rolls++;
            const hit = env.deps.ft_digsite_roll_table_attachment(master);
            if (hit === null) { misses++; if (specs.length === 0 && rolls === 1) return []; continue; }
            const spec = lootSpecForHit(hit, tier, env);
            if (spec === null) { misses++; continue; }
            specs.push(spec); value += Number(floatval(slot(spec, 'value_gp')));
        }
        return specs;
    });
}
